import { GripVertical, Pencil, Plus, Trash2 } from 'lucide-react';
import { useEffect, useState } from 'react';
import {
	DragDropContext,
	Draggable,
	type DraggableProvided,
	type DropResult,
} from 'react-beautiful-dnd';
import { StrictModeDroppable } from './StrictModeDroppable';

export type FormFieldValidation = {
	minLength?: number;
	maxLength?: number;
	min?: number;
	max?: number;
	pattern?: string;
};

export interface FormFieldOption {
	label: string;
	value: string;
}

export type FormField = {
	name: string; // Display identifier (for internal use)
	fieldId: string; // DAG context key (e.g., 'email', 'customer_name') - maps to context.data.form.<fieldId>
	label: string; // Display label for the field
	type: 'text' | 'email' | 'number' | 'textarea' | 'select' | 'checkbox' | 'radio' | 'file';
	required: boolean; // Whether the field is mandatory
	placeholder?: string; // Optional placeholder text
	defaultValue?: any; // Optional default value
	options?: FormFieldOption[]; // For select/radio fields with label/value pairs
	validation?: FormFieldValidation; // Optional validation rules
	checkboxOptions?: string[]; // For checkbox fields with label/value pairs
	selectOptions?: FormFieldOption[]; // For select fields with label/value pairs
	radioOptions?: FormFieldOption[]; // For radio fields with label/value pairs
	id?: string; // For radio fields with label/value pairs
};

interface FormBuilderProps {
	fields: FormField[];
	onChange: (fields: FormField[]) => void;
}

const FIELD_TYPES = [
	{ value: 'text', label: 'Text' },
	{ value: 'email', label: 'Email' },
	{ value: 'number', label: 'Number' },
	{ value: 'textarea', label: 'Textarea' },
	{ value: 'select', label: 'Select' },
	{ value: 'checkbox', label: 'Checkbox' },
	{ value: 'radio', label: 'Radio' },
	{ value: 'file', label: 'File' },
] as const;

export function FormBuilder({ fields, onChange }: FormBuilderProps) {
	const [editingField, setEditingField] = useState<string | null>(null);

	// Clean up any existing fields with invalid fieldIds
	const cleanFields = (fieldList: FormField[]): FormField[] => {
		return fieldList.map(field => {
			// If fieldId is missing or invalid, generate a new one
			if (!field.fieldId || typeof field.fieldId !== 'string' || field.fieldId.trim() === '') {
				const newFieldId = createFieldName(field.label || 'field');
				return {
					...field,
					fieldId: newFieldId,
					name: newFieldId,
				};
			}
			return field;
		});
	};

	// Clean fields on mount if needed
	useEffect(() => {
		const cleanedFields = cleanFields(fields);
		if (JSON.stringify(cleanedFields) !== JSON.stringify(fields)) {
			console.log('Cleaned invalid fields:', { original: fields, cleaned: cleanedFields });
			onChange(cleanedFields);
		}
	}, []);

	// Validate all fields have valid fieldIds
	const validateFields = (fieldList: FormField[]): boolean => {
		const invalidFields = fieldList.filter(
			field =>
				!field.fieldId || typeof field.fieldId !== 'string' || field.fieldId.trim() === ''
		);

		if (invalidFields.length > 0) {
			console.error('Found fields with invalid fieldIds:', invalidFields);
			console.error('All fields for debugging:', fieldList);
			return false;
		}

		return true;
	};

	const createFieldName = (label: string) => {
		const sanitized = label
			.toLowerCase()
			.replace(/[^a-z0-9\s]/g, '')
			.replace(/\s+/g, '_')
			.replace(/^_+|_+$/g, '');

		// Ensure we always have a valid fieldId
		const result = sanitized || 'field_' + Date.now();
		
		// Double-check that we have a valid result
		if (!result || result.trim() === '') {
			return 'field_' + Date.now();
		}
		
		return result;
	};

	const addField = (type: FormField['type']) => {
		// Generate a unique name by counting existing fields of the same type
		const existingFieldsOfType = fields.filter(f => f.type === type);
		const count = existingFieldsOfType.length + 1;

		const baseLabel = `New ${type} field`;
		const label = count > 1 ? `${baseLabel} ${count}` : baseLabel;
		const name = createFieldName(label);
		const fieldId = createFieldName(label);

		// Ensure we have valid fieldId
		if (!fieldId || fieldId.trim() === '') {
			console.error('Generated invalid fieldId:', { label, name, fieldId });
			return;
		}

		const newField: FormField = {
			name: fieldId,
			fieldId,
			label,
			type,
			required: false,
			placeholder: '',
			options:
				type === 'select' || type === 'radio'
					? [
							{ label: 'Option 1', value: 'option1' },
							{ label: 'Option 2', value: 'option2' },
					  ]
					: undefined,
		};

		const updatedFields = [...fields, newField];

		// Validate before updating
		if (validateFields(updatedFields)) {
			onChange(updatedFields);
			setEditingField(name);
		} else {
			console.error('Validation failed for new field');
		}
	};

	const updateField = (name: string, updates: Partial<FormField>) => {
		const updatedFields = fields.map(field => {
			if (field.name === name) {
				const updatedField = { ...field, ...updates };

				// If the label changed and fieldId wasn't manually set, auto-update fieldId
				// Only auto-update if the current fieldId is the same as the name (indicating it was auto-generated)
				if (updates.label && updates.label !== field.label && !updates.fieldId && field.fieldId === field.name) {
					const newFieldId = createFieldName(updates.label);
					// Ensure the new fieldId is valid
					if (newFieldId && newFieldId.trim() !== '') {
						updatedField.fieldId = newFieldId;
						updatedField.name = newFieldId;
					} else {
						console.warn('Could not generate valid fieldId for label:', updates.label);
					}
				}

				// Validate that fieldId is still valid after updates
				if (
					updatedField.fieldId &&
					(typeof updatedField.fieldId !== 'string' || updatedField.fieldId.trim() === '')
				) {
					console.error('Invalid fieldId detected after update:', updatedField);
					// Generate a fallback fieldId
					updatedField.fieldId = 'field_' + Date.now();
				}

				return updatedField;
			}
			return field;
		});

		// Validate before updating
		if (validateFields(updatedFields)) {
			onChange(updatedFields);
		} else {
			console.error('Validation failed for field update');
		}
	};

	const removeField = (name: string) => {
		const updatedFields = fields.filter(field => field.name !== name);

		// Validate remaining fields
		if (validateFields(updatedFields)) {
			onChange(updatedFields);
			if (editingField === name) {
				setEditingField(null);
			}
		} else {
			console.error('Validation failed after removing field');
		}
	};

	const onDragEnd = (result: DropResult) => {
		if (!result.destination) return;

		const items = Array.from(fields);
		const [reorderedItem] = items.splice(result.source.index, 1);
		items.splice(result.destination.index, 0, reorderedItem);

		// Validate after reordering
		if (validateFields(items)) {
			onChange(items);
		} else {
			console.error('Validation failed after reordering fields');
		}
	};

	return (
		<div className="space-y-4">
			{/* Field Type Buttons */}
			<div className="grid grid-cols-4 gap-2">
				{FIELD_TYPES.map(fieldType => (
					<button
						key={fieldType.value}
						type="button"
						onClick={() => addField(fieldType.value)}
						className="flex gap-2 items-center p-2 text-sm bg-gray-50 rounded border border-gray-200 transition-colors dark:bg-gray-700 hover:bg-gray-100 dark:hover:bg-gray-600 dark:border-gray-600"
					>
						<Plus className="w-4 h-4" />
						{fieldType.label}
					</button>
				))}
			</div>

			{/* Fields List */}
			{fields.length === 0 ? (
				<div className="p-8 text-center text-gray-500 bg-gray-50 rounded-lg border-2 border-gray-200 border-dashed dark:text-gray-400 dark:bg-gray-800 dark:border-gray-600">
					<p>No fields added yet. Click a field type above to get started.</p>
				</div>
			) : (
				<DragDropContext onDragEnd={onDragEnd}>
					<StrictModeDroppable droppableId="fields">
						{provided => (
							<div
								{...provided.droppableProps}
								ref={provided.innerRef}
								className="space-y-2"
							>
								{fields.map((field, index) => (
									<Draggable
										key={field.name}
										draggableId={field.name}
										index={index}
									>
										{(provided: DraggableProvided) => (
											<div
												ref={provided.innerRef}
												{...provided.draggableProps}
												className="p-4 bg-white rounded-lg border border-gray-200 dark:bg-gray-800 dark:border-gray-700"
											>
												<div className="flex gap-3 items-center">
													<div {...provided.dragHandleProps}>
														<GripVertical className="w-4 h-4 text-gray-400 dark:text-gray-500" />
													</div>

													<div className="flex-1">
														{editingField === field.name ? (
															<FieldEditor
																field={field}
																allFields={fields}
																onUpdate={updates =>
																	updateField(field.name, updates)
																}
																onCancel={() =>
																	setEditingField(null)
																}
															/>
														) : (
															<div className="flex justify-between items-center">
																<div className="flex-1">
																	<div className="font-medium text-gray-900 dark:text-white">
																		{field.label}{' '}
																		{field.required && (
																			<span className="text-red-500">
																				*
																			</span>
																		)}
																	</div>
																	<div className="text-sm text-gray-500 dark:text-gray-400">
																		{field.type} • fieldId:{' '}
																		{field.fieldId}
																	</div>
																</div>
																<div className="flex gap-2">
																	<button
																		onClick={() =>
																			setEditingField(
																				field.name
																			)
																		}
																		className="p-1 text-gray-400 transition-colors hover:text-blue-600"
																	>
																		<Pencil className="w-4 h-4" />
																	</button>
																	<button
																		onClick={() =>
																			removeField(field.name)
																		}
																		className="p-1 text-gray-400 transition-colors hover:text-red-600"
																	>
																		<Trash2 className="w-4 h-4" />
																	</button>
																</div>
															</div>
														)}
													</div>
												</div>
											</div>
										)}
									</Draggable>
								))}
								{provided.placeholder}
							</div>
						)}
					</StrictModeDroppable>
				</DragDropContext>
			)}
		</div>
	);
}

interface FieldEditorProps {
	field: FormField;
	allFields: FormField[];
	onUpdate: (updates: Partial<FormField>) => void;
	onCancel: () => void;
}

function FieldEditor({ field, allFields, onUpdate, onCancel }: FieldEditorProps) {
	const [localField, setLocalField] = useState(field);
	const [fieldIdError, setFieldIdError] = useState<string | null>(null);

	// Helper function to format and sanitize fieldId
	const formatFieldId = (input: string): string => {
		return input
			.toLowerCase()
			.replace(/[^a-z0-9\s]/g, '') // Remove special characters
			.replace(/\s+/g, '_') // Replace spaces with underscores
			.replace(/_+/g, '_') // Replace multiple underscores with single
			.replace(/^_+|_+$/g, ''); // Remove leading/trailing underscores
	};

	// Validate fieldId for uniqueness
	const validateFieldId = (fieldId: string): string | null => {
		if (!fieldId.trim()) {
			return 'Field ID is required';
		}

		if (fieldId.length < 2) {
			return 'Field ID must be at least 2 characters';
		}

		// Check for duplicates (exclude current field)
		const isDuplicate = allFields.some(f => f.name !== field.name && f.fieldId === fieldId);

		if (isDuplicate) {
			return `Field ID "${fieldId}" already exists. Please use a unique identifier.`;
		}

		return null;
	};

	// Validate on initial load
	useEffect(() => {
		const error = validateFieldId(localField.fieldId);
		setFieldIdError(error);
	}, []);

	const handleSave = () => {
		const error = validateFieldId(localField.fieldId);
		if (error) {
			setFieldIdError(error);
			return;
		}
		onUpdate(localField);
		onCancel();
	};

	const updateLocalField = (updates: Partial<FormField>) => {
		setLocalField(prev => ({ ...prev, ...updates }));
	};

	const handleFieldIdChange = (value: string) => {
		const formatted = formatFieldId(value);
		updateLocalField({ fieldId: formatted, name: formatted });

		// Validate in real-time
		const error = validateFieldId(formatted);
		setFieldIdError(error);
	};

	return (
		<div className="space-y-4">
			<div className="grid grid-cols-2 gap-4">
				<div>
					<label className="block mb-1 text-sm font-medium text-gray-700 dark:text-gray-300">
						Label
					</label>
					<input
						type="text"
						value={localField.label}
						onChange={e => updateLocalField({ label: e.target.value })}
						className="px-3 py-2 w-full text-gray-900 bg-white rounded-md border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white"
					/>
				</div>

				<div>
					<label className="block mb-1 text-sm font-medium text-gray-700 dark:text-gray-300">
						Type
					</label>
					<select
						value={localField.type}
						onChange={e =>
							updateLocalField({ type: e.target.value as FormField['type'] })
						}
						className="px-3 py-2 w-full text-gray-900 bg-white rounded-md border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white"
					>
						{FIELD_TYPES.map(type => (
							<option key={type.value} value={type.value}>
								{type.label}
							</option>
						))}
					</select>
				</div>
			</div>

			<div>
				<label className="block mb-1 text-sm font-medium text-gray-700 dark:text-gray-300">
					Field ID
					<span className="ml-2 text-xs text-gray-500 dark:text-gray-400">
						(Used by downstream DAG nodes as context.data.form.{localField.fieldId})
					</span>
				</label>
				<input
					type="text"
					value={localField.fieldId}
					onChange={e => handleFieldIdChange(e.target.value)}
					className={`px-3 py-2 w-full font-mono text-gray-900 bg-white rounded-md border dark:border-gray-600 dark:bg-gray-700 dark:text-white focus:outline-none focus:ring-2 focus:ring-blue-500 ${
						fieldIdError
							? 'border-red-500 focus:border-red-500 focus:ring-red-500'
							: 'border-gray-300 focus:border-blue-500'
					}`}
					placeholder="e.g., email, customer_name, price"
				/>
				{fieldIdError && (
					<p className="mt-1 text-sm text-red-600 dark:text-red-400">{fieldIdError}</p>
				)}
				<p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
					Only lowercase letters, numbers, and underscores allowed. Spaces will be
					auto-converted to underscores.
				</p>
			</div>

			<div>
				<label className="block mb-1 text-sm font-medium text-gray-700 dark:text-gray-300">
					Placeholder
				</label>
				<input
					type="text"
					value={localField.placeholder || ''}
					onChange={e => updateLocalField({ placeholder: e.target.value })}
					className="px-3 py-2 w-full text-gray-900 bg-white rounded-md border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white"
				/>
			</div>

			<div className="flex items-center">
				<input
					type="checkbox"
					id={`required-${field.name}`}
					checked={localField.required}
					onChange={e => updateLocalField({ required: e.target.checked })}
					className="mr-2"
				/>
				<label
					htmlFor={`required-${field.name}`}
					className="text-sm text-gray-700 dark:text-gray-300"
				>
					Required field
				</label>
			</div>

			{(localField.type === 'select' || localField.type === 'radio') && (
				<OptionsEditor
					options={localField.options || []}
					onChange={options => updateLocalField({ options })}
				/>
			)}

			<div className="flex gap-2">
				<button
					type="button"
					onClick={handleSave}
					disabled={!!fieldIdError}
					className={`px-4 py-2 text-white rounded-md transition-colors ${
						fieldIdError
							? 'bg-gray-400 cursor-not-allowed'
							: 'bg-blue-600 hover:bg-blue-700'
					}`}
				>
					Save
				</button>
				<button
					type="button"
					onClick={onCancel}
					className="px-4 py-2 text-white bg-gray-500 rounded-md hover:bg-gray-600"
				>
					Cancel
				</button>
			</div>
		</div>
	);
}

interface OptionsEditorProps {
	options: FormFieldOption[];
	onChange: (options: FormFieldOption[]) => void;
}

function OptionsEditor({ options, onChange }: OptionsEditorProps) {
	const [newOption, setNewOption] = useState({ label: '', value: '' });

	const addOption = () => {
		if (newOption.label.trim() && newOption.value.trim()) {
			onChange([...options, { ...newOption }]);
			setNewOption({ label: '', value: '' });
		}
	};

	const updateOption = (index: number, updates: Partial<FormFieldOption>) => {
		const updated = options.map((option, i) =>
			i === index ? { ...option, ...updates } : option
		);
		onChange(updated);
	};

	const removeOption = (index: number) => {
		onChange(options.filter((_, i) => i !== index));
	};

	const moveOption = (index: number, direction: 'up' | 'down') => {
		const newIndex = direction === 'up' ? index - 1 : index + 1;
		if (newIndex < 0 || newIndex >= options.length) return;

		const updated = [...options];
		[updated[index], updated[newIndex]] = [updated[newIndex], updated[index]];
		onChange(updated);
	};

	const handleKeyPress = (e: React.KeyboardEvent) => {
		if (e.key === 'Enter') {
			e.preventDefault();
			addOption();
		}
	};

	return (
		<div>
			<label className="block mb-2 text-sm font-medium text-gray-700 dark:text-gray-300">
				Options
			</label>

			{/* Existing Options */}
			<div className="mb-4 space-y-2">
				{options.map((option, index) => (
					<div
						key={index}
						className="flex gap-2 items-center p-2 bg-gray-50 rounded border border-gray-200 dark:bg-gray-700 dark:border-gray-600"
					>
						<div className="grid flex-1 grid-cols-2 gap-2">
							<input
								type="text"
								value={option.label}
								onChange={e => updateOption(index, { label: e.target.value })}
								placeholder="Display label"
								className="px-2 py-1 text-sm text-gray-900 bg-white rounded border border-gray-300 dark:border-gray-600 dark:bg-gray-800 dark:text-white"
							/>
							<input
								type="text"
								value={option.value}
								onChange={e => updateOption(index, { value: e.target.value })}
								placeholder="Value"
								className="px-2 py-1 font-mono text-sm text-gray-900 bg-white rounded border border-gray-300 dark:border-gray-600 dark:bg-gray-800 dark:text-white"
							/>
						</div>
						<div className="flex gap-1">
							<button
								type="button"
								onClick={() => moveOption(index, 'up')}
								disabled={index === 0}
								className="p-1 text-gray-400 hover:text-gray-600 disabled:opacity-50"
								title="Move up"
							>
								↑
							</button>
							<button
								type="button"
								onClick={() => moveOption(index, 'down')}
								disabled={index === options.length - 1}
								className="p-1 text-gray-400 hover:text-gray-600 disabled:opacity-50"
								title="Move down"
							>
								↓
							</button>
							<button
								type="button"
								onClick={() => removeOption(index)}
								className="p-1 text-red-400 hover:text-red-600"
								title="Remove option"
							>
								<Trash2 className="w-3 h-3" />
							</button>
						</div>
					</div>
				))}
			</div>

			{/* Add New Option */}
			<div className="flex gap-2">
				<div className="grid flex-1 grid-cols-2 gap-2">
					<input
						type="text"
						value={newOption.label}
						onChange={e => setNewOption(prev => ({ ...prev, label: e.target.value }))}
						onKeyPress={handleKeyPress}
						placeholder="Display label"
						className="px-2 py-1 text-sm text-gray-900 bg-white rounded border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white"
					/>
					<input
						type="text"
						value={newOption.value}
						onChange={e => setNewOption(prev => ({ ...prev, value: e.target.value }))}
						onKeyPress={handleKeyPress}
						placeholder="Value"
						className="px-2 py-1 font-mono text-sm text-gray-900 bg-white rounded border border-gray-300 dark:border-gray-600 dark:bg-gray-700 dark:text-white"
					/>
				</div>
				<button
					type="button"
					onClick={addOption}
					disabled={!newOption.label.trim() || !newOption.value.trim()}
					className="px-3 py-1 text-sm text-white bg-blue-600 rounded hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
				>
					<Plus className="w-4 h-4" />
				</button>
			</div>

			<p className="mt-2 text-xs text-gray-500 dark:text-gray-400">
				Press Enter to add options quickly. Use distinct values for proper form data
				handling.
			</p>
		</div>
	);
}
